import { Palette, Check } from "lucide-react";
import { useColorTheme, COLOR_THEMES } from "../../hooks/useColorTheme";
import { useRestaurant } from "../../context/RestaurantContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/**
 * ColorThemeToggle — compact palette button for the topbar.
 *
 * Smart behavior: if the current restaurant has its own theme override, the
 * topbar toggle updates THAT restaurant only. Otherwise it updates the user
 * default (applies to all restaurants without their own override). A subtle
 * "este local" hint surfaces in the dropdown header when the override is on.
 */
export function ColorThemeToggle() {
  const { currentRestaurant } = useRestaurant();
  const restaurantId = currentRestaurant?.restaurant_id || null;
  const { theme, hasRestaurantOverride, setTheme } = useColorTheme(restaurantId);
  const active = COLOR_THEMES.find((t) => t.id === theme) || COLOR_THEMES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center rounded-lg w-9 h-9 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Cambiar tema de color"
          data-testid="color-theme-toggle"
          data-compact="true"
        >
          <Palette className="w-4 h-4" />
          <span
            className="absolute bottom-1.5 right-1.5 w-2 h-2 rounded-full ring-2 ring-background"
            style={{ backgroundColor: active.swatch }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 p-2"
        data-testid="color-theme-menu"
      >
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold px-2">
          Tema de color
          {hasRestaurantOverride && (
            <span className="ml-1 normal-case text-primary tracking-normal" data-testid="color-theme-scope-badge">
              · este local
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="grid grid-cols-3 gap-1.5 p-1">
          {COLOR_THEMES.map((t) => {
            const isActive = t.id === theme;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTheme(t.id)}
                className={`group relative flex flex-col items-center gap-1.5 rounded-lg px-2 py-2.5 transition-colors ${
                  isActive
                    ? "bg-muted ring-1 ring-primary"
                    : "hover:bg-muted"
                }`}
                data-testid={`color-theme-option-${t.id}`}
                data-compact="true"
                title={t.description}
              >
                <span
                  className="relative w-7 h-7 rounded-full shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${t.swatch} 0%, ${t.swatchDark} 100%)`,
                  }}
                >
                  {isActive && (
                    <Check className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow" strokeWidth={3} />
                  )}
                </span>
                <span className="text-[11px] font-medium text-foreground leading-tight">
                  {t.name}
                </span>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ColorThemeToggle;
